import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { Document, QueryResult } from '../../core/store/models';
import * as turf from '@turf/turf';
import { ClosestAssetByLayerRequestDto, ClosestAssetCountResponse, ClosestAssetFilterDto } from '../store/impacted-assets';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class ImpactedAssetsApiService {
  constructor(private http: HttpService) {}

  public calculateClosestAssets(closestData: ClosestAssetFilterDto): Observable<Document[]> {
    // UI selected shapes, using drawing tools/lasso
    const url = `${apiBaseUrl}/v1/data/datasets/closestAsset`;
    return this.http.post(url, closestData);
  }

  public calculateClosestAssetsForLayer(closestData: ClosestAssetFilterDto, layerUId: string): Observable<QueryResult<Document[]>> {
    // RSD and Recipients assest selections, based on the layer and the selected documents
    const url = `${apiBaseUrl}/v1/data/datasets/closestAssetByLayer`;
    const layerState = closestData.layerStates.find(ls => ls.layerUId === layerUId);
    let featureCollection = closestData.locationGeoJson?.['filterGeo'] ? JSON.parse(closestData.locationGeoJson['filterGeo']) : null;
    if (!featureCollection) {
      return this.http.post(url, closestData);
    }

    try {
      // attempt to simplify the FeatureCollection if it contains mixed Feature types (e.g., Point, Polygon, MultiPolygon, etc.)
      const polygonFeatures = featureCollection.features.filter(
        (f: any) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
      );
      if (polygonFeatures.length > 0) {
        featureCollection = turf.union(turf.featureCollection(polygonFeatures));
      }
    } catch (error) {
      console.debug('Error simplifying locationGeoJson to FeatureCollection of polygons:', error);
    }

    let request: ClosestAssetByLayerRequestDto = {
      distanceGeoJson: closestData.distanceGeoJson,
      selectionLayerFilterDto: {
        queryMatchRuleDto: {
          ...layerState.queryMatchRuleDto
        },
        datasetUId: layerState.datasetUId,
        layerUId: layerUId
      },
      locationGeoJson: { 'filterGeo': JSON.stringify(featureCollection) },
      withinMeters: closestData.withinMeters,
      fromDate: closestData.fromDate,
      toDate: closestData.toDate
    };

    return this.http.post(url, request);
  }

  public getClosestAssetCountsByLayer(closestCountRequest: ClosestAssetFilterDto): Observable<ClosestAssetCountResponse[]> {
    const url = `${apiBaseUrl}/v1/data/datasets/closestAssetCountByLayer`;
    let featureCollection = closestCountRequest.locationGeoJson?.['filterGeo'] ? JSON.parse(closestCountRequest.locationGeoJson['filterGeo']) : null;
    if (!featureCollection) {
      return this.http.post(url, closestCountRequest);
    }

    try {
      const polygonFeatures = featureCollection.features?.filter(
        (f: any) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
      );
      if (polygonFeatures.length > 0) {
        featureCollection = turf.union(turf.featureCollection(polygonFeatures));
      }
    } catch (error) {
      console.debug('Error processing locationGeoJson for closest asset count request:', error);
    }

    const request: ClosestAssetFilterDto = {
      ...closestCountRequest,
      locationGeoJson: { 'filterGeo': JSON.stringify(featureCollection) }
    };
    return this.http.post(url, request);
  }
}
